import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Dispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';

import {
  BtnBar,
  Form,
  FormItem,
  Input,
  CtaBtn,
  SecondaryBtn,
  StickyFooter,
  Checkbox,
  FormRow,
  Select,
} from '@tmapy/style-guide';

import { useInput, useCheckbox } from '../../hooks';

import { setGeodesistsListFilter } from '../../redux/filter/actions';
import { geodesistBtnMessages, geodesistMessages, isActiveMessages } from './geodesistMessages';
import { GeodesistsListQuery } from '../../openapi/operations/geodesistsList';
import { GEODESIST_IS_ACTIVE } from '../constants';

type GeodesistFiltersProps = {
  filters: GeodesistsListQuery;
  dispatch: Dispatch;
};

export const GeodesistFilter: React.FC<GeodesistFiltersProps> = ({ filters, dispatch }) => {
  const formatMessage = useMessage();

  const search = useInput({
    name: 'search',
    initialValue: filters.search,
    label: formatMessage(geodesistMessages.search),
  });

  const official_number = useInput({
    name: 'official_number',
    initialValue: filters.official_number,
    label: formatMessage(geodesistMessages.officialNumber),
  });

  const has_certification_a = useCheckbox({
    name: 'has_certification_a',
    initialValue: !!filters.has_certification_a,
    label: 'A',
  });

  const has_certification_b = useCheckbox({
    name: 'has_certification_b',
    initialValue: !!filters.has_certification_b,
    label: 'B',
  });

  const has_certification_c = useCheckbox({
    name: 'has_certification_c',
    initialValue: !!filters.has_certification_c,
    label: 'C',
  });

  const is_active = useInput({
    name: 'is_active',
    initialValue: filters.is_active as any,
    items: useSelectMessages(isActiveMessages, GEODESIST_IS_ACTIVE),
    label: formatMessage(geodesistMessages.isActive),
  });

  const note = useInput({
    name: 'note',
    initialValue: filters.note__icontains,
    label: formatMessage(geodesistMessages.note),
  });

  const handleClearBtnClick = useCallback(() => {
    [search].forEach((formItem) => formItem?.clear?.());

    dispatch(setGeodesistsListFilter({}));
  }, [search, dispatch]);

  const handleSubmit = () => {
    dispatch(
      setGeodesistsListFilter({
        search: search.value,
        official_number: official_number.value,
        has_certification_a: has_certification_a.value ? true : undefined,
        has_certification_b: has_certification_b.value ? true : undefined,
        has_certification_c: has_certification_c.value ? true : undefined,
        is_active: is_active.value,
        note__icontains: note.value,
      }),
    );
  };

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-2'>
        <FormItem {...search}>
          <Input {...search} />
        </FormItem>
        <FormItem {...official_number}>
          <Input {...official_number} />
        </FormItem>
        <FormItem label={formatMessage(geodesistMessages.hasCertification)}>
          <FormRow>
            <Checkbox {...has_certification_a}>{has_certification_a.label}</Checkbox>
            <Checkbox {...has_certification_b}>{has_certification_b.label}</Checkbox>
            <Checkbox {...has_certification_c}>{has_certification_c.label}</Checkbox>
          </FormRow>
        </FormItem>
        <FormRow>
          <FormItem {...is_active}>
            <Select hasEmptyItem {...is_active} />
          </FormItem>
        </FormRow>
        <FormItem {...note}>
          <Input {...note} />
        </FormItem>
      </div>
      <StickyFooter>
        <BtnBar stretch>
          <CtaBtn type='submit'>
            <FormattedMessage {...geodesistBtnMessages.filterSubmit} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleClearBtnClick}>
            <FormattedMessage {...geodesistBtnMessages.clear} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </Form>
  );
};
